// Экран 2 — «Решение»: условие, поле ввода и вердикт на том же месте.
//
// Со-решателя в фазе 1 нет: ни лестницы подсказок, ни пошагового ввода, ни
// панели диалога. Вместо него разбор после проверки. Заголовка темы над задачей
// нет — и в контракте `today.plan` поэтому не отдаёт `skillId` в назначениях.
//
// **Вердикт показывается здесь, а не на отдельном экране** (11 сентября 2026).
// До этого ответ уводил на «Проверку», и вместе с экраном исчезало условие, по
// которому ученик только что считал: разбор приходилось читать, держа задачу в
// памяти, а старый экран из-за этого повторял условие второй раз. Теперь
// страница не меняется — под тем же условием встаёт полоса вердикта. Экранов
// стало четыре вместо пяти, и «Проверка» из `docs/01-core-loop.md` — это
// состояние этого экрана, а не свой.
//
// Второе состояние того же экрана — **пересмотр**: задача набора, закрытая
// раньше. Туда ведут черточки в шапке, и отвечать там нельзя: `today.review`
// отдаёт запись, а не задачу.

import { useCallback, useEffect, useRef, useState } from "react";
import { api, useMutation, useQuery, type Assignment, type SkillMove } from "../api";
import { nextActiveTwin } from "../lib/active-twin";
import type { Clock } from "../lib/clock";
import { useTaskDraft } from "../lib/task-draft";
import { recordVerdict } from "../lib/coach-state";
import { CoachNote } from "../ui/CoachNote";
import { Formula } from "../lib/Formula";
import { Choices } from "../ui/Choices";
import { Figure } from "../ui/Figure";
import { IconBook, IconCompass } from "../ui/Icons";
import { lineSeed, verdictLine } from "../lib/verdict-lines";
import { Moves } from "../ui/Moves";
import { Next, ReviewNav } from "../ui/Next";
import { Scratch } from "../ui/Scratch";
import { Verdict } from "../ui/Verdict";

/** Что стоит под условием: своя только что сделанная проверка или запись. */
type Feedback = {
  raw: string;
  correct: boolean;
  answerDisp: string | null;
  solution: string[];
  /**
   * Движение навыков. `null` — пересмотр: «навык 61 → 68» это разница двух
   * снимков состояния, и по строке журнала она не восстанавливается.
   */
  moves: SkillMove[] | null;
  twinItemKey: string | null;
  /** Зерно заголовка. Берётся один раз: на перерисовках строка не меняется. */
  seed: number;
};

export function Solve({
  learnerKey,
  planDate,
  itemKey,
  clock,
  assignments,
  activeTwinItemKeys,
  onAnswered,
  onSolve,
  onOpenDay,
  onToday,
  onOpenTheory,
  onOpenAtlas,
  onSkill,
}: {
  learnerKey: string;
  /** День набора, к которому относится задача, даже после полуночи. */
  planDate: string;
  itemKey: string;
  /** Одни часы на все заходы в эту задачу; atlas и разбор их не сбрасывают. */
  clock: Clock;
  /** Набор дня целиком: отсюда соседи задачи и её место в ряду. */
  assignments: Assignment[];
  /** Незаконченные близнецы этого набора, сохранённые поверх смены задач. */
  activeTwinItemKeys: string[];
  onAnswered: (answer: {
    planDate: string;
    itemKey: string;
    twinItemKey: string | null;
  }) => void;
  onSolve: (itemKey: string) => void;
  onOpenDay: () => void;
  onToday: () => void;
  /**
   * Разбор темы этой задачи. Возврат — сюда же, на ту же задачу: см.
   * комментарий у кнопки.
   */
  onOpenTheory: (skillId: string, backToItemKey: string) => void;
  /** Read-only связи навыка; возврат сохраняет точную задачу и её черновик. */
  onOpenAtlas: (skillId: string, backToItemKey: string) => void;
  onSkill?: (skillId: string) => void;
}) {
  const item = useQuery(api.today.item, { learnerKey, itemKey });
  const reportSkill = useRef(onSkill);
  reportSkill.current = onSkill;
  useEffect(() => { if (item) reportSkill.current?.(item.skillId); }, [item?.skillId]);
  const answer = useMutation(api.today.answer);
  const { draft, update: updateDraft, clear: clearDraft } = useTaskDraft(itemKey);
  const raw = draft.raw;
  const setRaw = useCallback(
    (next: string): void => updateDraft((current) => ({ ...current, raw: next })),
    [updateDraft],
  );
  const setScratch = useCallback(
    (scratch: typeof draft.scratch): void =>
      updateDraft((current) => ({ ...current, scratch })),
    [updateDraft],
  );

  const [busy, setBusy] = useState(false);
  const [unparsable, setUnparsable] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [checked, setChecked] = useState<Feedback | null>(null);
  // Ошибок подряд к этому ответу — тренер говорит со второй (src/lib/coach-state.ts).
  const [missStreak, setMissStreak] = useState(0);
  const errorBox = useRef<HTMLDivElement | null>(null);

  // Задача с выбором обоснования: поля ввода у неё нет, ответом уезжает
  // идентификатор варианта. Признак считается из `vkind`, а не из наличия
  // `choices`: вид ответа — источник истины, а список может приехать пустым
  // при рассинхроне клиента с пересобранным пулом, и тогда честнее показать
  // пустой выбор, чем молча вернуть текстовое поле, которое сервер отвергнет.
  const picking = item?.vkind === "reason";

  // Какую реплику тренер скажет до ответа. Вызов — всегда; новый навык — на
  // первой его задаче в наборе (ни одна задача «new» ещё не закрыта).
  const current = assignments.find((a) => a.itemKey === itemKey);
  const coachBefore: "challenge" | "newSkill" | null =
    current?.kind === "challenge"
      ? "challenge"
      : current?.kind === "new" && !assignments.some((a) => a.kind === "new" && a.done)
        ? "newSkill"
        : null;

  useEffect(() => {
    setUnparsable(false);
    setSubmitError(false);
    setChecked(null);
  }, [itemKey]);

  // Пауза, пока вкладка скрыта. Уход из вкладки — сигнал куда лучше, чем
  // бездействие: пятнадцатилетний решает в тетрадке и по четыре минуты не
  // касается клавиатуры, а «нет нажатий — стоп» наказывало бы ровно то
  // поведение, которого мы хотим.
  //
  // Экран может размонтироваться ради atlas или разбора. Тогда зачтённое время
  // стоит, но сами часы остаются у App и продолжают wall от первого показа.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") clock.pause();
      else clock.resume();
    };
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clock.pause();
    };
  }, [clock]);

  const at = assignments.findIndex((a) => a.itemKey === itemKey);
  // Задача закрыта — либо этой проверкой, либо раньше. Своя проверка главнее:
  // `markDone` патчит набор той же мутацией, и подписка приносит `done: true`
  // в ту же секунду, когда вердикт уже на экране.
  const closed = at >= 0 && assignments[at]!.done;
  const reviewing = checked === null && closed;
  const past = useQuery(api.today.review, reviewing ? { learnerKey, itemKey } : "skip");

  const feedback: Feedback | null =
    checked ??
    (past != null
      ? {
          raw: past.raw,
          correct: past.verdict === "correct",
          answerDisp: past.answerDisp,
          solution: past.solution,
          moves: null,
          // Зерно — метка времени попытки: одна и та же закрытая задача
          // открывается одним и тем же заголовком, сколько ни возвращайся.
          seed: past.at,
          twinItemKey: null,
        }
      : null);

  const prev = at > 0 ? assignments[at - 1]!.itemKey : null;
  /** Следующая по порядку — шаг пересмотра, закрыта она или нет. */
  const after = at >= 0 && at + 1 < assignments.length ? assignments[at + 1]!.itemKey : null;
  /** Следующее незакрытое назначение — ход занятия вперёд. */
  const undone = assignments.find((a) => !a.done && a.itemKey !== itemKey)?.itemKey ?? null;
  // Близнец не входит в assignments, но должен пережить уход по верхним
  // черточкам. На самом близнеце не ведём «Далее» обратно в него же.
  const pendingTwin = nextActiveTwin(activeTwinItemKeys, itemKey);
  const forward = checked?.twinItemKey ?? pendingTwin ?? undone;
  const forwardIsTwin =
    forward !== null &&
    (forward === checked?.twinItemKey || forward === pendingTwin);

  const submit = (): void => {
    if (busy || raw.trim().length === 0 || !item || feedback !== null) return;
    if (navigator.onLine === false) {
      setSubmitError(true);
      window.requestAnimationFrame(() => errorBox.current?.focus());
      return;
    }
    setBusy(true);
    setUnparsable(false);
    setSubmitError(false);
    void answer({
      learnerKey,
      planDate,
      itemKey,
      raw,
      ms: clock.elapsed(),
      msWall: clock.wall(),
    })
      .then((result) => {
        setBusy(false);
        // Неразобранный ввод — не провал и не вердикт: опечатка возвращается в
        // то же поле, задача не закрывается, состояние не двигается.
        if (result.verdict === "unparsable") {
          setUnparsable(true);
          return;
        }
        setMissStreak(recordVerdict(planDate, result.verdict === "correct"));
        setChecked({
          raw,
          correct: result.verdict === "correct",
          answerDisp: result.answerDisp,
          solution: result.solution,
          moves: result.moves,
          twinItemKey: result.twinItemKey,
          seed: lineSeed(),
        });
        clearDraft();
        onAnswered({ planDate, itemKey, twinItemKey: result.twinItemKey });
      })
      .catch((error: unknown) => {
        setBusy(false);
        setSubmitError(true);
        console.error(error);
        window.requestAnimationFrame(() => errorBox.current?.focus());
      });
  };

  return (
    <div className="cols">
      <div className="col">
        {/* Условие стоит на месте и после проверки: разбор ссылается на его
            числа, и без него «22 · 3 = 66» не с чем сверить. */}
        <div className="card">
          {/*
            Кнопка разбора стоит в углу условия и появляется только там, где
            разбор написан.

            **Почему она не нарушает запрет на заголовок темы.** Над условием
            по-прежнему не написано, какая это тема: кнопка называет себя, а не
            задачу, и пока её не нажали, выбор метода тренируется ровно так же.
            Тему ученик узнаёт, только если сам решил, что не помнит правила, —
            то есть ценой одного нажатия и ухода со страницы.

            **Почему это не подсказка.** Подсказка отвечает на «что здесь
            делать», а страница разбора — на «как эта тема вообще устроена»:
            того же числа в ней нет и быть не может, задачи порождены шаблоном.
            Потолок подсказки она не трогает, и попытка от неё не портится:
            открытие ложится в `theoryViews`, а не в `attempts`.

            **Возврат ведёт обратно на эту задачу, а не в набор.** Ученик ушёл
            читать посреди решения; кнопка «Решать эту тему», уводящая в новую
            порцию из трёх задач, бросила бы недорешённую — поэтому на разборе,
            открытом отсюда, её нет.
          */}
          <div className="card__head">
            <div className="card__label">Условие</div>
            <div className="row">
              {item?.theory === true ? (
                <button
                  className="btn btn--icon"
                  title="Правило, разобранный пример и типичные ошибки этой темы"
                  onClick={() => onOpenTheory(item.skillId, itemKey)}
                >
                  <IconBook size={17} />
                  Разбор темы
                </button>
              ) : null}
              {item ? (
                <button
                  className="btn btn--icon"
                  title="Место текущего навыка в структуре курса"
                  onClick={() => onOpenAtlas(item.skillId, itemKey)}
                >
                  <IconCompass size={17} />
                  Связи навыка
                </button>
              ) : null}
            </div>
          </div>
          {item ? (
            <div className="statement">
              <Formula source={item.statement} chip />
              {item.figure === undefined ? null : <Figure source={item.figure} />}
            </div>
          ) : (
            <div className="skeleton" style={{ marginTop: 14, height: 26 }} />
          )}
        </div>

        {feedback !== null ? (
          <>
            <Verdict
              correct={feedback.correct}
              line={verdictLine(feedback.correct, feedback.seed)}
              raw={feedback.raw}
              answerDisp={feedback.answerDisp}
              solution={feedback.solution}
              unclear={{ learnerKey, itemKey, where: "Задача" }}
            />
            {/* Тренер после второй ошибки подряд — только на свежем ответе. */}
            {checked !== null && !checked.correct && missStreak >= 2 ? (
              <CoachNote situation="missStreak" seedParts={[learnerKey, planDate, itemKey]} />
            ) : null}
            {checked !== null ? (
              <Next
                learnerKey={learnerKey}
                forward={forward}
                twin={forwardIsTwin}
                prev={prev}
                onSolve={onSolve}
                onOpenDay={onOpenDay}
                onToday={onToday}
              />
            ) : (
              <ReviewNav prev={prev} next={after} onSolve={onSolve} onToday={onToday} />
            )}
          </>
        ) : reviewing ? (
          <div className="card">
            {past === undefined ? (
              <div className="skeleton" style={{ height: 22 }} />
            ) : (
              <>
                {/* Набор говорит «закрыта», а попытки за сегодня нет: так
                    выглядит день, перешагнувший полночь в открытой вкладке. */}
                <div className="card__label">Задача закрыта</div>
                <div className="muted" style={{ marginTop: 10, fontSize: 15 }}>
                  Записи ответа за сегодня нет — похоже, задача закрыта в другой день.
                </div>
                <div className="row row--actions" style={{ marginTop: 18 }}>
                  <button className="link link--quiet" onClick={onToday}>
                    К набору
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
          {/*
            Тренер до ответа: перед задачей формата экзамена и на первой задаче
            нового навыка — там, где живой тренер сказал бы слово
            (решение 30 октября 2026).
          */}
          {coachBefore === null ? null : (
            <CoachNote situation={coachBefore} seedParts={[learnerKey, planDate, itemKey]} />
          )}
          <div className="card">
            <div className="card__label">Ответ</div>

            {picking ? (
              /* У выбора поля ввода нет вовсе. Поставить его рядом «на всякий
                 случай» значило бы предложить два способа ответить на один
                 вопрос, из которых сервер принимает ровно один. */
              <>
                <Choices
                  options={item.choices ?? []}
                  value={raw}
                  onPick={setRaw}
                  disabled={busy}
                />
                <div className="row row--actions" style={{ marginTop: 16 }}>
                  <button
                    className="btn btn--primary"
                    disabled={busy || raw.length === 0}
                    onClick={submit}
                  >
                    {busy ? "Проверяю" : "Проверить"}
                  </button>
                </div>
              </>
            ) : (
            <div className="answer" style={{ marginTop: 14 }}>
              <span className="answer__label">Ответ</span>
              <input
                className={`field${unparsable ? " field--bad" : ""}`}
                value={raw}
                aria-label="Ответ на задачу"
                aria-invalid={unparsable || submitError}
                autoFocus
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={item?.inputHint ?? ""}
                onChange={(event) => setRaw(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") submit();
                }}
              />
              <button
                className="btn btn--primary"
                disabled={busy || raw.trim().length === 0 || !item}
                onClick={submit}
              >
                {busy ? "Проверяю" : "Проверить"}
              </button>
            </div>
            )}

            {unparsable ? (
              <div className="notice" role="status" style={{ marginTop: 12 }}>
                {picking ? (
                  // У выбора «не разобрано» означает не опечатку, а рассинхрон
                  // с пересобранным пулом: набрать такое ученик не может.
                  <>Вариант не распознан — похоже, задача обновилась. Откройте её заново.</>
                ) : (
                  <>
                    Не разобрано. Поле принимает {item?.inputHint}. Корни в виде √ или ±
                    не набираются — ни один верный ответ здесь не иррационален.
                  </>
                )}
              </div>
            ) : null}

            {submitError ? (
              <div
                ref={errorBox}
                className="notice solve-submit-error"
                role="alert"
                tabIndex={-1}
              >
                <div>Ответ не отправлен. Проверьте соединение и попробуйте ещё раз.</div>
                <button className="btn" disabled={busy} onClick={submit}>
                  Отправить ещё раз
                </button>
              </div>
            ) : null}
          </div>
          </>
        )}
      </div>

      <div className="col">
        {/*
          Черновик — в правой колонке, как в прототипе. Сначала он стоял
          кнопкой под полем ответа, и это была моя ошибка места: колонка —
          то, что у ученика под рукой весь день, и искал он его там.

          В колонке стоит сама панель, а не вход в неё: «от руки» и «столбик»
          пишутся прямо здесь — лист постоянного размера, свёрнутое окно стоит
          на его середине. Во весь экран уходят кнопкой, и доска только так:
          её панель инструментов в 340 пикселей не помещается физически.

          Ключ по задаче: новая задача получает чистый лист. «Одна задача —
          один лист», и черновик предыдущей под новым условием читался бы как
          её часть.
        */}
        {item ? (
          <Scratch
            key={itemKey}
            statement={item.statement}
            draft={draft.scratch}
            onChange={setScratch}
          />
        ) : null}

        {feedback === null ? (
          <div className="card card--dashed">
            <div className="card__label">Как это проверяется</div>
            <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
              Проверка серверная и мгновенная. Порядок корней не важен, дробь и
              десятичная — одно и то же число, кириллическая «х» принимается как «x».
            </div>
            <div className="faint" style={{ marginTop: 12, lineHeight: 1.6 }}>
              Подсказок нет: разбор открывается после проверки — и при верном
              ответе тоже.
            </div>
          </div>
        ) : (
          <>
            {feedback.moves !== null ? (
              <div className="card">
                <div className="card__label">Что изменилось в модели</div>
                <Moves moves={feedback.moves} scope="answer" />
              </div>
            ) : null}

            <div className="card card--dashed">
              <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
                {feedback.moves === null
                  ? "Это запись: задача уже закрыта, и состояние она больше не двигает. Движение навыков показывается один раз — в секунду проверки."
                  : feedback.correct
                    ? "Разбор есть и у верного ответа: сошедшийся ответ и верный метод разные вещи. Трудность растёт вместе с владением навыком, а не с одного ответа."
                    : "Похожая задача идёт сразу, пока ошибка ещё в голове. Разбор под вердиктом — единственная помощь в фазе 1."}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
